import { exceptionToString, makeError } from '@mimir/common/util/exception';
import type { Observable } from '@mimir/common/util/observable';
import type { Cleanable } from '@mimir/common/util/cleanable';
import type { PromiseSource } from '@mimir/common/async/async';
import { defer } from '@mimir/common/async/async';
import type { Logger } from '@mimir/common/types/logger';
import type { FuzzyBounds, FuzzyValue, } from '@mimir/common/util/fuzzyNumbers';
import { addFuzzy, compareFuzzy, fractionToFuzzy, humanReadableRange, humanReadableRanges, integerToFuzzy, maxFuzzy, minFuzzy, multiplyFuzzy, isRangeCovered, shrinkRanges, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import type { MediaSourceOps, MediaSourceProviderOps, SourceBufferEventType, SourceBufferOps } from './mediaSourceOps';
import { PrefixedLogger } from '@mimir/common/log/prefixedLogger';
import { Fraction } from '@mimir/common/util/fractionType';
import { isAbortError } from '@mimir/common/util/abortControl';

export interface CmafSegment {
  /**
   * Bytes of the CmafSegment
   */
  bytes: Uint8Array,

  /**
   * Approximate time stamp range of the segment (in seconds). For the init
   * segment this should be set to null.
   */
  timeRange: FuzzyBounds | null,
}

export interface CmafSegmentIterator {
  /**
   * Get the next CMAF segment. This will block forever unless either the next
   * segment is provided OR the internal state of the iterator requests a
   * re-initialization. Re-initialization may occur in scenarios where the
   * underlying segment iterator state has changed so that the current CMAF
   * segments are no longer valid (TS soloing channel changed or FLAC downmix
   * changes).
   */
  next(bufferedRanges: FuzzyBounds[]): Promise<CmafSegment | 'closed'>;

  /**
   * The start position of this iterator after adjusting for segment
   * boundaries. For instance when creating the iterator the MSE buffering
   * may request an iterator at position 3.5, but if the segment size is 2
   * then the first position returned by the iterator will be 2.
   */
  startPosition(): FuzzyValue;

  /**
   * This can be called from any thread and while a next call is in progress.
   * It will cause any pending and future calls to next to return 'closed'.
   */
  close(): void;
}

export interface CmafSegmentSource {
  /**
   * Used for logging
   */
  humanReadableDescription(): string;

  /**
   * Used for initializing the MSE source buffer
   */
  codec(): string;

  /**
   * Passing in start time null means starting at the first segment.
   */
  createIterator(
    startTime: FuzzyValue | null,
    invalidateBuffer: () => void,
  ): Promise<CmafSegmentIterator>;

  /**
   * Returns the current duration of the media. TODO: Not sure if this is the
   * correct level to abstract this. One Mimir item has a single defined
   * duration, but may have multiple CmafSegmentSources. Because of this it
   * might actually belong to the interface above to provide this information.
   */
  duration(): FuzzyValue;
}

export interface BufferingSettings {
  /**
   * Allowed forward buffering. How much forward the user has to seek in order
   * to trigger creation of a new iterator. If seeking forward within this range
   * then the current iterator will be used until this point in time is reached.
   */
  allowedForwardSeekBuffering: FuzzyValue;


  /**
   * Ratio of the total amount of maximum allowed buffered media to keep as a
   * rewind buffer. For instance if set to 50% then as you play forward there
   * will be equal length buffer before and after the play head once the
   * browser's buffer quota has been reached
   */
  rewindBufferBalance: Fraction,

  /**
   * Chrome quirk workaround: Chrome will automatically evict from the source
   * buffer when appending new buffers. Not only is this wrong, but also when
   * playing forward Chrome will evict everything behind the current demux
   * position. Since there is a discrepancy between the demux position and
   * the current decoded position you end up with the situation where Chrome
   * evicts media at the beginning of the buffer range BETWEEN the player
   * position and the iterator buffer position. Normally this would cause the
   * buffering logic to think that there is a range missing between the
   * current iterator buffering position and the player position. This in turn
   * will cause us to close the current iterator and recreate it even though
   * it is not really needed. In order to combat this we allow a tolerance of
   * missing buffered range directly in front of the player position when
   * playing forward. This setting controls the size of this tolerance (in
   * seconds). Reasonable value that seems to do the trick here is 2 seconds.
   */
  earlyCacheEvictionTolerance: FuzzyValue,

  /**
   * Buffered ranges reported by the MSE are not always accurate and can cause
   * playback to get stuck if assuming these are accurate. Because of this we
   * have to apply a safety margin to the reported buffered ranges. This can
   * be configured here. Experiments have proven that 1 second seems to be a
   * fitting value.
   */
  bufferRangeSafetyMargin: FuzzyValue,

  /**
   * Soft limit on the max buffered duration. In the end the browser will
   * have a hard limit that we can go over, but in certain scenarios like
   * only using a player for getting frame snapshots, we don't want to
   * buffer as much as the browser allows.
   */
  maxBufferedDurationSoftLimit: FuzzyValue | null,
}

export function startMseBufferingAndReturnUrl(
  logger: Logger,
  mediaSourceProviderOps: MediaSourceProviderOps,
  cmafSegmentSources: CmafSegmentSource[],
  playerPosition: Observable<number>,
  abortSignal: AbortSignal,
  settings: BufferingSettings,
  onError?: (error: any) => void,
): string {
  const mediaSource = mediaSourceProviderOps.createMediaSource();

  let abortController : AbortController | null = null;

  let mediaSourceOpen = false;

  mediaSource.onSourceClose(() => {
    logger.debug('MSE: onsourceclose');
    mediaSourceOpen = false;
    abortController?.abort();
  });

  mediaSource.onSourceOpen(() => {
    logger.debug('MSE: onsourceopen');
    mediaSourceOpen = true;

    if (abortController !== null) {
      logger.warn('MSE: onsourceopen called multiple times on the same MSE');
      abortController?.abort();
      abortController = null;
    }

    abortController = new AbortController();
    for (const cmafSegmentSource of cmafSegmentSources) {
      void startBufferingStream(
        logger,
        mediaSource,
        cmafSegmentSource,
        playerPosition,
        abortSignal,
        settings,
      ).catch((error) => {
        const id = cmafSegmentSource.humanReadableDescription();
        if (isAbortError(error)) {
          if (mediaSourceOpen) {
            logger.warn(`Unexpected abort of startBufferingStream for ${id}`);
          }
        } else {
          logger.error(`Failed to buffer ${id}: ${exceptionToString(error)}`);
          abortController?.abort();
          if (onError !== undefined) onError(error);
        }
      });
    }
  });

  return mediaSource.url();
}

interface IteratorState {
  iterator: CmafSegmentIterator,
  // The end PTS of the last segment put into the source buffer from this
  // iterator. Note that this is the PTS reported by the CMAF segment provider
  // and may not accurately match the buffered range reported by the MSE
  lastCachedPosition: FuzzyValue,

  lastSegmentDuration: FuzzyValue,

  // What player position this iterator was created for
  createdForPlayerPosition: FuzzyValue,
}

async function startBufferingStream(
  parentLogger: Logger,
  mediaSource: MediaSourceOps,
  cmafSegmentSource: CmafSegmentSource,
  playerPosition: Observable<number>,
  abortSignal: AbortSignal,
  settings: BufferingSettings,
): Promise<void> {
  let aborted = false;
  const cleanables : Cleanable[] = [];

  let iteratorState : IteratorState | null = null;

  const onAbort = () => {
    aborted = true;
    abortSignal.removeEventListener('abort', onAbort);
    iteratorState?.iterator?.close();
    iteratorState = null;
  };
  abortSignal.addEventListener('abort', onAbort);

  // This is needed so that we can know if we are moving forward or backwards
  // when the player position changes.
  let previousPlayerPosition : number = playerPosition.currentValue();

  const logger = new PrefixedLogger(parentLogger, `[${cmafSegmentSource.humanReadableDescription()}]`);

  // Since we need to do all manipulations of the source buffer from the same
  // thread in order to avoid invalid state errors we can't evict all directly
  // from the iterator evictAll callbacks. Instead we set this to true so that
  // the buffering thread can evict all on the next iteration.
  let evictAll = false;

  try {
    const codecString = cmafSegmentSource.codec();
    const sourceBuffer = new AsyncSourceBuffer(mediaSource.addSourceBuffer(codecString));

    cleanables.push(playerPosition.observe((position) => {
      // When the player position changes, we need to go through several checks to find
      // out if we need to invalidate the current iterator.
      const fuzzyPosition : FuzzyValue = { approximate: position };

      if (iteratorState === null) {
        logger.debug(`   no iterator. playerPosition=${position.toFixed(2)}`);
        return;
      }

      const movingForward =
        (position >= previousPlayerPosition) &&
        (compareFuzzy(fuzzyPosition, iteratorState.lastCachedPosition) < 0);

      previousPlayerPosition = fuzzyPosition.approximate;

      if (compareFuzzy(addFuzzy(iteratorState.lastCachedPosition, settings.allowedForwardSeekBuffering), fuzzyPosition) < 0) {
        logger.info(`playerPosition moved too far after the current buffering position making the iterator invalid: lastCachedPosition = ${iteratorState.lastCachedPosition.approximate.toFixed(2)}, playerPosition=${position.toFixed(2)}`);
        iteratorState.iterator.close();
        iteratorState = null;
        return;
      }

      if (compareFuzzy(fuzzyPosition, iteratorState.lastCachedPosition) >= 0) {
        logger.debug(`playerPostition moved to right after the current buffering position meaning that we should wait for the iterator to buffer more. lastCachedPosition = ${iteratorState.lastCachedPosition.approximate.toFixed(2)}, playerPosition=${position.toFixed(2)}`);
        return;
      }

      // If a CMAF source does not account for the fact that its last segment
      // has a shorter duration than the other segments this means effectively
      // two things:
      //
      //  1) The CMAF segment source reports a duration up to a segment
      //     duration too long.
      //  2) The last segment returned from the CMAF segment iterator will
      //     be reported too long which in turn makes "lastCachedPosition"
      //     extend past the end of the actual media.
      //
      // Step 2 will cause us to incorrectly reset the iterator because there
      // is buffered data missing between player position and
      // "lastCachedPosition". In order to avoid this, we clamp the valid range
      // check end to a whole segment before the duration reported by the
      // segment source.

      const validEnd = subtractFuzzy(iteratorState.lastCachedPosition, settings.bufferRangeSafetyMargin);
      const shortDuration = subtractFuzzy(cmafSegmentSource.duration(), iteratorState.lastSegmentDuration);
      const clampedEnd = minFuzzy(validEnd, shortDuration);

      const validRange : FuzzyBounds = {
        start: fuzzyPosition,
        end: clampedEnd,
      };

      const bufferedRanges = getBufferedRangesFromSourceBuffer(sourceBuffer);

      if (isRangeCovered(bufferedRanges, validRange)) {
        logger.debug(`playerPosition changed but iterator is still valid. ranges=${humanReadableRanges(bufferedRanges)}, valid=${humanReadableRange(validRange)}, playerPosition=${position.toFixed(2)}, movingForward=${movingForward}, lastCachedPosition=${iteratorState.lastCachedPosition.approximate.toFixed(2)}`);
        return;
      }

      // Workaround for Chrome quirk: Chrome may evict samples right in front
      // of the player head when playing forward. So if we are playing forward
      // and don't have the full range covered we check again with an offset
      // applied to the start.
      if (movingForward) {
        const startMargin = (movingForward ? settings.earlyCacheEvictionTolerance : integerToFuzzy(0));
        const validRange : FuzzyBounds = {
          start: addFuzzy(fuzzyPosition, startMargin),
          end: clampedEnd,
        };
        if (isRangeCovered(bufferedRanges, validRange)) {
          logger.debug(`playerPosition changed but iterator is still valid (Chrome workaround). ranges=${humanReadableRanges(bufferedRanges)}, valid=${humanReadableRange(validRange)}, playerPosition=${position.toFixed(2)}, movingForward=${movingForward}, lastCachedPosition=${iteratorState.lastCachedPosition.approximate.toFixed(2)}`);
          return;
        }
      }

      // playerPosition changed and there are unbuffered areas between it and the current iterator position meaning that we need to clear the iterator
      logger.info(`playerPosition change made iterator invalid. ranges=${humanReadableRanges(bufferedRanges)}, valid=${humanReadableRange(validRange)}, playerPosition=${position.toFixed(8)}, lastCachedPosition=${iteratorState.lastCachedPosition.approximate.toFixed(8)}, movingForward=${movingForward}, largestSeenDuration=${largestSeenDuration}`);
      iteratorState.iterator.close();
      iteratorState = null;
    }));

    let largestSeenDuration = 0;

    while (true) {
      if (evictAll) {
        logger.debug('evicting all cache');
        evictAll = false;
        iteratorState?.iterator?.close();
        iteratorState = null;
        await sourceBuffer.evictAll();
        continue;
      }

      abortSignal.throwIfAborted();

      const playerTime : FuzzyValue = { approximate: playerPosition.currentValue() };
      const durationInSeconds = cmafSegmentSource.duration().approximate;

      // Chrome bug: If we set the duration to a value which is less than
      // one of the currently buffered ranges chrome will spontaneously
      // trigger an eviction of any media after this point in the buffer.
      // Aside from potentially losing some frames at the end of the stream
      // we also end up with the sourceBuffer going into the "updating"
      // state causing the next appendBuffer call to fail because the source
      // buffer is currently busy. In order to avoid this we increase the
      // duration if any buffered range goes beyond it.
      //
      // We try to avoid losing actual data close to the live edge by clamping
      // how small we set the duration based on the maximum buffered position
      // we've seen so far. This appears to not be sufficient though and Chrome
      // will still go into the clipping part of the code even though we don't
      // set the duration to a smaller value than buffered range reported by
      // Chrome itself. So we need to also have the safety check below

      const lastBuffered = getLastbufferedPtsOrZero(sourceBuffer);
      largestSeenDuration = Math.max(durationInSeconds, lastBuffered, largestSeenDuration);

      try {
        mediaSource.setLiveSeekableRange(0.0, largestSeenDuration);
      } catch (ex) {
        // TODO: Check the exception type. Do not log for the expected case
        logger.debug(`Failed to setLiveSeekableRange. This is expected to happen every now and then if the MSE is being updated. Error: ${exceptionToString(ex)}`);
      }

      try {
        mediaSource.duration = largestSeenDuration;
      } catch (ex) {
        // TODO: Check the exception type. Do not log for the expected case
        logger.debug(`Failed to MSE duration. This is expected to happen every now and then if the MSE is being updated. Error: ${exceptionToString(ex)}`);
      }

      await sourceBuffer.ensureChromeIsNotInTheProcessOfClippingTheSourceBuffer(logger);

      if (iteratorState === null) {
        if (aborted) {
          return;
        }
        logger.info(`Creating a new iterator from playerTime: ${playerTime.approximate.toFixed(2)}`);

        const iterator =
          await cmafSegmentSource.createIterator(
            subtractFuzzy(playerTime, settings.bufferRangeSafetyMargin),
            () => {
              logger.debug('iterator wants to evict all cache');
              iteratorState?.iterator.close();
              iteratorState = null;
              // We can't evict from the buffer directly here since it is
              // unsafe to mutate the source buffer from different threads.
              evictAll = true;
            }
          );

        logger.debug('Iterator created.');

        iteratorState = {
          iterator,
          lastCachedPosition: iterator.startPosition(),
          lastSegmentDuration: integerToFuzzy(0),
          createdForPlayerPosition: playerTime,
        };
      }

      const bufferedRanges = getBufferedRangesFromSourceBuffer(sourceBuffer);
      const bufferedRangesWithSafetyMargin = shrinkRanges(bufferedRanges, settings.bufferRangeSafetyMargin);

      logger.debug('Iterator next.');
      const iteratorResult = await iteratorState.iterator.next(bufferedRangesWithSafetyMargin);
      logger.debug('Iterator next complete.');

      if (iteratorResult === 'closed') {
        logger.debug('iterator closed');
        // This thread requested the iterator to be closed. This usually means
        // that the player position changed and that we concluded we would need
        // a new iterator.
        iteratorState?.iterator?.close();
        iteratorState = null;
        continue;
      }

      const segment : CmafSegment = iteratorResult;

      logger.debug(`Handling segment from iterator: ${(segment.timeRange === null) ? 'init-segment' : humanReadableRange(segment.timeRange)}`);

      // We use the iterator as indicator whether we should keep trying to
      // buffer this segment. If the iterator has been reset it means that
      // this segment is no longer valid and we should not buffer it.
      while (iteratorState) {
        const playerTime = playerPosition.currentValue();

        /**
         * We now need to figure out the hot range. The hot range is the range
         * before and after the player position where we do not evict from the
         * buffer cache.
         *
         * There is no way to query the MSE of the max buffer size so we just
         * assume that the current buffer usage is the max buffer size. The
         * reason we can do this is that we never attempt to evict anything
         * unless we get QuotaExceededError anyway. So under normal operation
         * this assumption will hold whenever we actually need the hot
         * range.
         *
         * The size of the hot range will be max buffer size minus the
         * incoming segment duration (So that we just have enough space for
         * it). How much of the hot range is before the player position and
         * how much is after the player position is controlled by the rewind
         * buffer balance buffering setting.
         */
        const segmentDuration = (segment.timeRange !== null) ? (subtractFuzzy(segment.timeRange.end, segment.timeRange.start)) : integerToFuzzy(0);
        const totalBuffered = bufferedRanges.reduce((total, range) => addFuzzy(total, subtractFuzzy(range.end, range.start)), integerToFuzzy(0));
        let hotRangeSize = subtractFuzzy(totalBuffered, segmentDuration);

        const softLimit =
          (settings.maxBufferedDurationSoftLimit !== null) ?
            maxFuzzy(settings.maxBufferedDurationSoftLimit, segmentDuration) :
            null;

        if (softLimit !== null) {
          hotRangeSize = minFuzzy(softLimit, hotRangeSize);
        }

        const rewindBufferSize = multiplyFuzzy(hotRangeSize, fractionToFuzzy(settings.rewindBufferBalance));
        const forwardBufferSize = subtractFuzzy(hotRangeSize, rewindBufferSize);

        logger.silly(`totalBuffered = ${totalBuffered.approximate.toFixed(2)}, rewindBufferSize = ${rewindBufferSize.approximate.toFixed(2)}, forwardBufferSize = ${forwardBufferSize.approximate.toFixed(2)}`);

        /**
         * We must NEVER evict between the current player position and the
         * current buffering position since then we'd have to reset the
         * iterator and buffer the same range again.
         */
        const hotRangeEndMin = addFuzzy(iteratorState.lastCachedPosition, settings.bufferRangeSafetyMargin);

        /**
         * Requested forward buffer end
         */
        const requestedForwardBufferEnd = addFuzzy({ approximate: playerTime }, forwardBufferSize);
        const hotRange : FuzzyBounds = {
          start: subtractFuzzy({ approximate: playerTime }, rewindBufferSize),
          end: maxFuzzy(hotRangeEndMin, requestedForwardBufferEnd),
        };

        logger.debug(`evict and buffer. hotRange=${humanReadableRange(hotRange)}`);
        if (await maybeEvictMediaAndBufferSegment(logger, sourceBuffer, segment.bytes, hotRange, softLimit)) {
          logger.debug('evict and buffer: OK');
          if ((iteratorState !== null) && (segment.timeRange !== null)) {
            iteratorState.lastCachedPosition = segment.timeRange.end;
            iteratorState.lastSegmentDuration = subtractFuzzy(segment.timeRange.end, segment.timeRange.start);
          }
          break;
        }
        logger.debug('evict and buffer: FULL');

        // We get to this point when there is no more room in the SourceBuffer
        // and the hot range prevented us from freeing up space. If that is the
        // case then the only way to progress with the buffering is for the
        // player position to change so that we either can free up more space
        // behind the play head or that it is a seek in which case we reset
        // the iterator and thus the hot area.
        if (playerTime === playerPosition.currentValue()) {
          logger.debug('blocked by player position');
          await playerPosition.waitForChange(abortSignal);
        } else {
          logger.debug('player position changed while trying to evict media');
        }
      }
    }
  } catch (error: any) {
    if (!isAbortError(error)) {
      logger.error(`Buffering failed: ${exceptionToString(error)}`);
      throw error;
    }
  } finally {
    cleanables.splice(0).forEach((cleanable) => cleanable.cleanup());
    iteratorState?.iterator?.close();
    iteratorState = null;
    logger.info('Buffering thread stopped.');
  }
}

function getBufferedRangesFromSourceBuffer(sourceBuffer: AsyncSourceBuffer): FuzzyBounds[] {
  let timeRanges = sourceBuffer.buffered();
  let ranges : FuzzyBounds[] = [];
  for (let i = 0; i < timeRanges.length; i++) {
    ranges.push({
      start: { approximate: timeRanges.start(i) },
      end: { approximate: timeRanges.end(i) },
    });
  }
  return ranges;
}

function getLastbufferedPtsOrZero(sourceBuffer: AsyncSourceBuffer): number {
  const bufferedRanges = getBufferedRangesFromSourceBuffer(sourceBuffer);
  return bufferedRanges.at(bufferedRanges.length - 1)?.end?.approximate ?? 0;
}

function totalBufferedDuration(ranges: FuzzyBounds[]): FuzzyValue {
  return ranges.reduce(
    (total, range) => addFuzzy(total, subtractFuzzy(range.end, range.start)),
    integerToFuzzy(0)
  );
}

function getBufferedDurationFromSourceBuffer(sourceBuffer: AsyncSourceBuffer): FuzzyValue {
  const ranges = getBufferedRangesFromSourceBuffer(sourceBuffer);
  return totalBufferedDuration(ranges);
}

function checkSoftLimit(
  logger: Logger,
  sourceBuffer: AsyncSourceBuffer,
  softLimit: FuzzyValue | null,
): boolean {
  if (softLimit === null) {
    return true;
  }
  const totalBuffered = getBufferedDurationFromSourceBuffer(sourceBuffer);
  // We cannot have a soft limit smaller than a single segment
  const result = (compareFuzzy(totalBuffered, softLimit) <= 0);
  logger.debug(`checkSoftLimit: totalBuffered=${totalBuffered.approximate.toFixed(2)}, softLimit=${softLimit.approximate.toFixed(2)}, result=${result}`);
  return result;
}

async function tryAppendBuffer(
  logger: Logger,
  sourceBuffer: AsyncSourceBuffer,
  bytes: Uint8Array,
  softLimit: FuzzyValue | null,
): Promise<boolean> {
  if (!checkSoftLimit(logger, sourceBuffer, softLimit)) {
    return false;
  }
  return await sourceBuffer.appendBuffer(bytes);
}


async function maybeEvictMediaAndBufferSegment(
  logger: Logger,
  sourceBuffer: AsyncSourceBuffer,
  bytes: Uint8Array,
  hotRange: FuzzyBounds,
  softLimit: FuzzyValue | null,
): Promise<boolean> {
  try {
    if (await tryAppendBuffer(logger, sourceBuffer, bytes, softLimit)) {
      return true;
    }

    await sourceBuffer.evictRange(0, hotRange.start.approximate);

    if (await tryAppendBuffer(logger, sourceBuffer, bytes, softLimit)) {
      return true;
    }

    const bufferedLength = sourceBuffer.buffered().length;
    if (bufferedLength === 0) {
      throw new Error('Capacity reached even though mse has no buffered ranges');
    }
    const bufferedEnd = sourceBuffer.buffered().end(bufferedLength - 1);
    await sourceBuffer.evictRange(hotRange.end.approximate, bufferedEnd);

    return await tryAppendBuffer(logger, sourceBuffer, bytes, softLimit);
  } catch (error) {
    logger.error(`Failed to buffer segment: ${exceptionToString(error)}`);
    throw error;
  }
}

/**
 *  The MSE SourceBuffer has a synchronous API. This class works as an async
 *  wrapper around the SourceBuffer API.
 */
class AsyncSourceBuffer {
  private ready: PromiseSource<void> | null = null;
  private lastUpdateFailed : boolean = false;
  constructor(private sourceBuffer: SourceBufferOps) {
    const checkReady = (_s: string) => {
      if (sourceBuffer.updating || this.ready === null) {
        return;
      }
      const deferred = this.ready.deferred;
      this.ready = null;
      deferred.resolve();
    };

    const eventTypes : SourceBufferEventType[] = ['abort', 'updateend'];
    for (const type of eventTypes) {
      sourceBuffer.addEventListener(type, () => checkReady(type));
    }

    sourceBuffer.addEventListener('error', () => {
      this.lastUpdateFailed = true;
      checkReady('error');
    });
  }

  public async appendBuffer(bytes: Uint8Array): Promise<boolean> {
    try {
      if (this.sourceBuffer.updating) {
        throw new Error('Source buffer is updating');
      }
      this.sourceBuffer.appendBuffer(bytes);
      await this.waitAndCheckError();
      return true;
    } catch (ex) {
      if (ex != null) {
        const exa = ex as { name?: unknown };
        if (exa.name != 'QuotaExceededError') {
          throw ex;
        }
      }
      return false;
    }
  }

  public async evictAll() {
    const buffered = this.sourceBuffer.buffered;
    const count = buffered.length;
    if (count === 0) {
      return;
    }
    const start = buffered.start(0);
    const end = buffered.end(count - 1);
    this.sourceBuffer.remove(start, end);
    await this.waitAndCheckError();
  }

  public async evictRange(start: number, end: number) {
    if (start >= end) {
      return;
    }
    this.sourceBuffer.remove(start, end);
    await this.waitAndCheckError();
  }

  public buffered(): TimeRanges {
    return this.sourceBuffer.buffered;
  }

  public async ensureChromeIsNotInTheProcessOfClippingTheSourceBuffer(logger: Logger) {
    if (this.sourceBuffer.updating) {
      logger.debug('Successfully evaded potential bug caused by bad Chrome Media Source Extension implementation');
      await this.waitAndCheckError();
    }
  }

  private async waitAndCheckError(): Promise<void> {
    if (!this.sourceBuffer.updating) {
      return;
    }

    if (this.ready !== null) {
      throw new Error('ready already set');
    }
    this.ready = defer<void>();
    await this.ready.promise;

    if (this.lastUpdateFailed) {
      throw makeError('Buffer update failed', 'UpdateError');
    }
  }
}
